'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'

export interface SelectedAddressPlace {
  name: string
  address: string
  structuredAddress: {
    streetAddress?: string
    locality?: string
    region?: string
    postalCode?: string
    country?: string
    coordinates?: { lat: number, lng: number }
  }
  phone: string
  googlePlaceId: string
}

interface Suggestion {
  placeId: string
  name: string
  secondaryText: string
}

interface Props {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelect: (place: SelectedAddressPlace) => void
  labels: { loading: string, noResults: string }
  locale?: string
  proximity?: { lat: number, lng: number }
  placeholder?: string
  disabled?: boolean
  id?: string
  name?: string
  ariaLabel?: string
  inputClassName?: string
  inputStyle?: React.CSSProperties
  autoComplete?: string
}

const DEBOUNCE_MS = 300
const MIN_INPUT_LENGTH = 3

const newSessionToken = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {}
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** Texto que se muestra en el input tras elegir una dirección. */
export const getAddressLabel = (place: SelectedAddressPlace): string =>
  place.structuredAddress?.streetAddress || place.address || place.name

const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  labels,
  locale = 'es',
  proximity,
  placeholder,
  disabled,
  id,
  name,
  ariaLabel,
  inputClassName,
  inputStyle,
  autoComplete = 'shipping address-line1'
}: Props) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [loading, setLoading] = useState(false)

  const sessionTokenRef = useRef<string>(newSessionToken())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Valor que rellenará el padre tras una selección: el cambio de `value` no
  // debe relanzar la búsqueda (solo esa vez).
  const selectedValueRef = useRef<string | null>(null)
  // Si el padre pasa un valor inicial (p. ej. la dirección ya guardada del bar),
  // no lanzamos búsqueda al montar: solo cuando el usuario toca el input.
  const initialValueRef = useRef(value)
  const hasInteractedRef = useRef(false)

  const close = useCallback(() => {
    setOpen(false)
    setSuggestions([])
    setActiveIndex(-1)
    setLoading(false)
  }, [])

  const fetchSuggestions = useCallback(async (input: string) => {
    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    try {
      const res = await fetch('/api/public/places/autocomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input,
          locale,
          sessionToken: sessionTokenRef.current,
          ...(proximity ? { proximity } : {})
        }),
        signal: controller.signal
      })
      if (controller.signal.aborted) return
      if (!res.ok) {
        close()
        return
      }
      const data = await res.json()
      if (controller.signal.aborted) return
      setSuggestions(data.suggestions || [])
      setActiveIndex(-1)
      setOpen(true)
    } catch (err: any) {
      if (err?.name !== 'AbortError') close()
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }, [locale, proximity, close])

  useEffect(() => {
    // No buscar con el valor inicial precargado (solo tras interacción real).
    if (!hasInteractedRef.current && value === initialValueRef.current) return

    const trimmed = value.trim()
    if (trimmed.length < MIN_INPUT_LENGTH || disabled) {
      close()
      return
    }

    if (selectedValueRef.current) {
      const wasSelection = selectedValueRef.current === trimmed
      selectedValueRef.current = null
      if (wasSelection) return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(trimmed)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [value, disabled, fetchSuggestions, close])

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const fetchDetails = useCallback(async (suggestion: Suggestion) => {
    try {
      const res = await fetch('/api/public/places/details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          placeId: suggestion.placeId,
          locale,
          sessionToken: sessionTokenRef.current
        })
      })
      if (!res.ok) {
        close()
        return
      }
      const data = await res.json()
      if (data.place) {
        const place = data.place as SelectedAddressPlace
        selectedValueRef.current = getAddressLabel(place).trim()
        onSelect(place)
      }
    } catch {
      // La selección es opcional: si details falla no rompemos el flujo.
    } finally {
      // La sesión de Places termina: se regenera el token.
      sessionTokenRef.current = newSessionToken()
      close()
    }
  }, [locale, onSelect, close])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) {
      if (e.key === 'Escape') close()
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex(prev => (prev + 1) % suggestions.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex(prev => (prev - 1 + suggestions.length) % suggestions.length)
        break
      case 'Enter':
        e.preventDefault()
        if (activeIndex >= 0 && suggestions[activeIndex]) {
          fetchDetails(suggestions[activeIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        close()
        break
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasInteractedRef.current = true
    onChange(e)
  }

  const showNoResults = open && !loading && suggestions.length === 0 && value.trim().length >= MIN_INPUT_LENGTH

  return (
    <div style={{ position: 'relative' }}>
      <MapPin
        size={16}
        aria-hidden='true'
        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#a3a3a3', pointerEvents: 'none', zIndex: 1 }}
      />
      <input
        id={id}
        name={name}
        type='text'
        role='combobox'
        aria-expanded={open}
        aria-autocomplete='list'
        aria-controls={id ? `${id}-listbox` : undefined}
        aria-activedescendant={activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(close, 150)}
        className={inputClassName}
        style={{ paddingLeft: 34, ...inputStyle }}
      />

      {open && (
        <ul
          id={id ? `${id}-listbox` : undefined}
          role='listbox'
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 30,
            margin: 0,
            padding: '4px 0',
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 260,
            overflowY: 'auto'
          }}
        >
          {loading && (
            <li style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#6b7280' }}>{labels.loading}</li>
          )}

          {!loading && suggestions.map((s, i) => (
            <li
              key={s.placeId}
              id={id ? `${id}-option-${i}` : undefined}
              role='option'
              aria-selected={i === activeIndex}
              onMouseDown={(e) => {
                e.preventDefault()
                fetchDetails(s)
              }}
              onMouseEnter={() => setActiveIndex(i)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                cursor: 'pointer',
                background: i === activeIndex ? '#ecfdf5' : 'transparent'
              }}
            >
              <MapPin size={14} aria-hidden='true' style={{ color: '#6b7280', flexShrink: 0 }} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: '0.9rem', color: '#1a1a1a' }}>{s.name}</span>
                {s.secondaryText && (
                  <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>{s.secondaryText}</span>
                )}
              </span>
            </li>
          ))}

          {showNoResults && (
            <li style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#6b7280' }}>{labels.noResults}</li>
          )}
        </ul>
      )}
    </div>
  )
}

export default AddressAutocomplete
